import { useEffect, useState } from 'react';
import { ChatLeft } from 'react-bootstrap-icons';
import { useParams } from 'react-router-dom';

import { Button, Checkbox, List, Select, Space, Typography } from 'antd';

import CommentItem from '@/components/shared/CommentItem';
import EmptyState from '@/components/shared/EmptyState';
import { useBreakpoints } from '@/hooks/useBreakpoints';
import { commentsApi } from '@/services/comments';
import styled, { StyledProp, css } from '@/styled';

import Actions from './Actions';

const orderByMenu = ['Todos os comentários', 'Mais recentes', 'Mais antigos'];

const orderMapper = [
  { allComments: true, order: 'ASC', orderBy: 'created_at' },
  { order: 'DESC', orderBy: 'created_at' },
  { order: 'ASC', orderBy: 'created_at' }
];

const ListComment = ({ className }: StyledProp) => {
  const { id } = useParams();
  const [ownComments, setOwnComments] = useState(false);
  const [fetchComments, { data, isFetching, isUninitialized }] = commentsApi.endpoints.getComments.useLazyQuery();
  const breakpoints = useBreakpoints();

  const handleOrder = (order: number) => {
    fetchComments({ lessonId: id, params: { ...data?.page, ...orderMapper[order], page: 1 } });
  };

  const toggleOwnComments = () => {
    setOwnComments(state => !state);
    fetchComments({ lessonId: id, params: { ...data?.page, ownComments: !ownComments, page: 1 } });
  };

  const fetchNewPage = () => {
    fetchComments({ lessonId: id, params: { ...data?.page, page: data?.page.page + 1 } });
  };

  useEffect(() => {
    if (isUninitialized) {
      fetchComments({ lessonId: id, params: { page: 1, allComments: true, order: 'DESC', orderBy: 'created_at' } });
    }
  }, [fetchComments, id, isUninitialized]);

  return (
    <div className={className}>
      <Space size={16} className='header'>
        {breakpoints.up('md') && <Typography.Text className='count'>{data?.total_rows} Comentários</Typography.Text>}
        {breakpoints.up('md') && <Typography.Text>•</Typography.Text>}
        <Select
          disabled={isFetching}
          onChange={handleOrder}
          defaultValue={0}
          placeholder={<Typography.Text strong>Exibir por</Typography.Text>}
          options={orderByMenu.map((label, value) => ({ value, label }))}
          size='large'
        />
        <Space>
          <Checkbox checked={ownComments} onChange={toggleOwnComments} />
          <Typography.Text>Apenas meus comentários</Typography.Text>
        </Space>
      </Space>

      <List
        loading={isFetching}
        itemLayout='horizontal'
        rowKey={item => item.id}
        locale={{
          emptyText: (
            <EmptyState
              icon={<ChatLeft />}
              title='Os comentários dessa aula aparecerão aqui.'
              description='Você poderá visualizar com facilidade os comentários da aula.'
            />
          )
        }}
        loadMore={
          data?.total_pages > 1 && (
            <Button disabled={isFetching || data?.page?.page >= data?.total_pages} onClick={fetchNewPage}>
              Carregar mais comentários
            </Button>
          )
        }
        dataSource={data?.data}
        renderItem={comment => (
          <CommentItem comment={comment}>
            <Actions {...comment} isOwnComments={ownComments} isReply={false} />
          </CommentItem>
        )}
      />
    </div>
  );
};

export default styled(ListComment)(
  ({ theme }) => css`
    min-height: 300px;

    .header {
      margin-top: ${theme.space.pxToRem(16)};
      flex-wrap: wrap;
    }

    .count {
      white-space: nowrap;
    }

    .commentActions {
      width: 100%;
    }

    .ant-select {
      min-width: 200px;
    }

    .ant-select-selector {
      border: none !important;
      background-color: transparent !important;
    }

    .ant-list-header {
      border-block-end: 1px solid ${theme.palette.alpha(theme.palette.gray[0], 0.12)};
    }

    button {
      height: 40px;
      width: 100%;
    }

    span.ant-typography {
      color: ${theme.colorTextSecondary};
    }

    .ant-spin-nested-loading .ant-spin-container::after {
      background: ${theme.variables.loadingOverlayColor};
    }
  `
);
